import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Select, { components as reactSelectComponents } from 'react-select';

import CheckboxField from 'components/application/CheckboxField';
import {
  DropdownIndicator,
  MultiValueContainer,
} from 'components/application/CollectionSelect';

import { resourceShape } from 'components/helpers/serialisableResources';

export default function SelectAndInputFieldsForNestedAttribute({
  assignedPersonsAtRisk,
  nestedNameAttribute,
  personsAtRiskOptions,
  shouldInitiallySelectDefaultRisk,
}) {
  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const assignedPersonsAtRiskNames = assignedPersonsAtRisk.data.map(
    (personAtRisk) => personAtRisk.attributes.name,
  );

  const [currentPersonsAtRisk, setCurrentPersonsAtRisk] = useState(
    shouldInitiallySelectDefaultRisk ?
      [personsAtRiskOptions[0]]
    : assignedPersonsAtRiskNames,
  );

  const personsAtRiskOptionsAndAssignments = [
    ...new Set([...personsAtRiskOptions, ...assignedPersonsAtRiskNames]),
  ];

  const formattedPersonAtRiskOptions = personsAtRiskOptionsAndAssignments.map(
    (option) => ({ label: option, value: option, name: option }),
  );

  const formattedAssignedPersonAtRiskValues = currentPersonsAtRisk.map(
    (personAtRisk) => ({
      label: personAtRisk,
      name: personAtRisk,
      value: personAtRisk,
    }),
  );

  const assignedPersonsAtRiskToCreate = currentPersonsAtRisk.filter(
    (personAtRisk) => !assignedPersonsAtRiskNames.includes(personAtRisk),
  );

  const assignedPersonsAtRiskToDelete = assignedPersonsAtRiskNames.filter(
    (personAtRisk) => !currentPersonsAtRisk.includes(personAtRisk),
  );

  // Maintaining an index to ensure collections of hidden inputs used to send params to the server have unique index numbers and so do not overwrite each other on the backend
  let nestedAttributeIndex = 0;

  const findAssignedPersonsAtRiskId = (personAtRisk) =>
    assignedPersonsAtRisk.data &&
    assignedPersonsAtRisk.data.find(
      (assignedPerson) => assignedPerson.attributes.name === personAtRisk,
    ).id;

  const handleOptionsChange = (_selected, actionMeta) => {
    const { value } = actionMeta.option;

    if (currentPersonsAtRisk.includes(value)) {
      const currentPersonsAtRiskWithoutValue = currentPersonsAtRisk.filter(
        (personAtRisk) => personAtRisk !== value,
      );

      setCurrentPersonsAtRisk(currentPersonsAtRiskWithoutValue);
    } else {
      setCurrentPersonsAtRisk([...currentPersonsAtRisk, value]);
    }
  };

  return (
    <>
      <Select
        backspaceRemovesValue={false}
        className='collection-select__select-container'
        classNamePrefix='collection-select'
        closeMenuOnSelect={false}
        components={{
          DropdownIndicator,
          MultiValueContainer,
          Option,
          ValueContainer,
        }}
        customMargin='tw-mb-4'
        hideSelectedOptions={false}
        isClearable={false}
        isMulti={true}
        isSearchable={false}
        maxMenuHeight='unset'
        onChange={handleOptionsChange}
        options={formattedPersonAtRiskOptions}
        placeholder={i18nTShared('selectPersons')}
        styles={{
          valueContainer: (provided, _state) => ({
            ...provided,
            padding: '9px 12px !important',
          }),
        }}
        value={formattedAssignedPersonAtRiskValues}
      />
      {assignedPersonsAtRiskToCreate.map((personAtRisk) => {
        const hiddenInputToCreateAssignedPersonsAtRisk = (
          <div key={nestedAttributeIndex}>
            <input
              name={`${nestedNameAttribute}[assigned_persons_at_risks_attributes][${nestedAttributeIndex}][name]`}
              type='hidden'
              value={personAtRisk}
            />
          </div>
        );

        nestedAttributeIndex++;

        return hiddenInputToCreateAssignedPersonsAtRisk;
      })}
      {assignedPersonsAtRiskToDelete.map((personAtRisk) => {
        const hiddenInputToDeleteAssignedPersonsAtRisk = (
          <div key={nestedAttributeIndex}>
            <input
              name={`${nestedNameAttribute}[assigned_persons_at_risks_attributes][${nestedAttributeIndex}][id]`}
              type='hidden'
              value={findAssignedPersonsAtRiskId(personAtRisk)}
            />
            <input
              name={`${nestedNameAttribute}[assigned_persons_at_risks_attributes][${nestedAttributeIndex}][_destroy]`}
              type='hidden'
              value={1}
            />
          </div>
        );

        nestedAttributeIndex++;

        return hiddenInputToDeleteAssignedPersonsAtRisk;
      })}
    </>
  );
}

SelectAndInputFieldsForNestedAttribute.propTypes = {
  assignedPersonsAtRisk: PropTypes.shape({
    data: PropTypes.arrayOf(resourceShape('assignedPersonsAtRisk')),
  }),
  nestedNameAttribute: PropTypes.string.isRequired,
  personsAtRiskOptions: PropTypes.array.isRequired,
  shouldInitiallySelectDefaultRisk: PropTypes.bool.isRequired,
};

function Option(props) {
  return (
    <reactSelectComponents.Option {...props} key={props.label}>
      <CheckboxField
        checked={props.isSelected}
        disabled={false}
        isInputLabelSibling={true}
        label={props.label}
        labelProps={{ className: 'tw-mb-0' }}
        name={props.data.name}
        onChange={props.setValue}
        value={props.value}
      />
    </reactSelectComponents.Option>
  );
}

function ValueContainer({ children, ...props }) {
  return (
    <reactSelectComponents.ValueContainer {...props}>
      <div className='collection-select__input-values tw-m-0 tw-h-5 tw-truncate'>
        {children}
      </div>
    </reactSelectComponents.ValueContainer>
  );
}
